import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import { computed } from 'mobx'

/**
 * Specifies what the "exit" buttons in the application are supposed to do.
 */
export type ExitMethod =
  | NavigateToDashboardExitMethod
  | ReturnToPartnerExitMethod

/**
 * Protocol that exit methods must implement.
 */
interface ExitMethodProtocol {
  /**
   * The type of method.
   */
  readonly type: ExitMethod['type']

  /**
   * The return URL.
   */
  readonly returnUrl: string

  /**
   * Label to show on buttons.
   */
  readonly returnLabel: string

  /**
   * Whether the exit method should be visible in the UI.
   *
   * There are cases where we don't want to show the user a way to exit.
   * For example, Robinhood shows their own exit button, so we won't show any
   * on our side.
   */
  readonly isVisible: boolean

  /**
   * Performs the navigation.
   */
  execute(): void
}

/**
 * Navigates to the dashboard.
 */
export class NavigateToDashboardExitMethod implements ExitMethodProtocol {
  /** @inheritDoc */
  readonly type = 'NavigateToDashboard' as const

  /** @inheritDoc */
  readonly returnLabel = 'Back to home'

  /** @inheritDoc */
  readonly returnUrl = links.home()

  /** @inheritDoc */
  readonly isVisible = true

  constructor(private readonly _navigate: NavigateFn) {}

  /**
   * Navigates to the dashboard.
   */
  execute() {
    this._navigate(links.home())
  }
}

/**
 * Options returned from the reactive getter.
 */
export interface ReturnToPartnerExitMethodReturnOptions {
  returnLabel: string
  returnUrl: string
  isVisible: boolean
}

/**
 * Navigates back to the partner.
 */
export class ReturnToPartnerExitMethod implements ExitMethodProtocol {
  /** @inheritDoc */
  readonly type = 'ReturnToPartner' as const

  constructor(
    private _getReturnOptions: () => ReturnToPartnerExitMethodReturnOptions,
  ) {}

  /** @inheritDoc */
  @computed get returnUrl() {
    return this._getReturnOptions().returnUrl
  }

  /** @inheritDoc */
  @computed get returnLabel() {
    return this._getReturnOptions().returnLabel
  }

  /** @inheritDoc */
  @computed get isVisible() {
    return this._getReturnOptions().isVisible
  }

  /**
   * Whether we support delegating exit to the host, e.g. a partner which
   * is iframing the application.
   */
  get supportsDelegatedExit() {
    // If we're running as an iframe, it's possible that consumers
    // are listening for the `close-iframe` action via the `message` event.
    return window.parent !== window.self
  }

  /**
   * Navigates back to the partner.
   */
  execute() {
    const returnUrl = this.returnUrl || 'about:blank'

    // If not embedded, just redirect the current window.
    if (!this.supportsDelegatedExit) {
      window.location.href = returnUrl
      return
    }

    // Notify that we want to exit.
    window.parent?.postMessage({ action: 'close-iframe' }, '*')

    // Give the consumer (if any) enough time to handle the message,
    // but redirect after a small delay in case they don't to be safe,
    // or if the consumer isn't listening.
    setTimeout(() => {
      window.location.href = returnUrl
    }, 10)
  }
}
